/* eslint-disable jsx-a11y/alt-text */
import React from 'react'
import { Link } from 'react-router-dom'
import StarIcon from '../icons/star.svg'
import StarlineIcon from '../icons/star-line.svg'
import buildBuyLink from '../utils/buildBuyLink.ts'
import TokenLogoImage from './TokenLogoImage.jsx'

// Money: K/M/B with 2 fixed decimals (duplicate to preserve exact rendering)
const formatMoney1 = n => {
  const x = Number(n) || 0
  const abs = Math.abs(x)
  let v = x
  let s = ''
  if (abs >= 1e9) {
    v = x / 1e9
    s = 'B'
  } else if (abs >= 1e6) {
    v = x / 1e6
    s = 'M'
  } else if (abs >= 1e3) {
    v = x / 1e3
    s = 'K'
  }
  const str = Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
  return str + s
}

const wlKey = (address, chainId) => {
  const addr = String(address || '').toLowerCase()
  const c = chainId ?? ''
  return c === '' ? addr : `${addr}:${c}`
}
const isWatched = (wl, address, chainId) => {
  const addr = String(address || '').toLowerCase()
  if (!Array.isArray(wl) || !addr) return false
  const key = wlKey(addr, chainId)
  return wl.includes(key) || wl.includes(addr)
}

export default function GraduatedCoins({
  graduatedPaintDone,
  gradSkelHoldDone,
  graduatedItems,
  graduatedByFees,
  gradVisibleCount,
  setGradVisibleCount,
  GRAD_PAGE_SIZE,
  toggleWatchlist,
  watchlist
}) {
  return (
    <aside className="block md:sticky md:top-3">
      <div className="rounded-lg border border-[#2a2d36] bg-[#171a21] px-4 md:px-5 py-3 max-h-[520px] overflow-auto">
        <div className="flex items-center gap-3 mb-2 min-w-0">
          <span
            className="inline-block h-3 w-3 rounded-full bg-[#7CFC98]"
            aria-hidden="true"
          />
          <div className="truncate">
            <div className="truncate text-[13px] leading-5 text-[#f8ffe8] font-semibold">
              graduated{' '}
              <span className="font-normal text-[#f8ffe8]/80">coins</span>
            </div>
            <div className="hidden sm:block text-[12px] leading-4 text-[#f8ffe8]/50 truncate">
              listed & earning fees
            </div>
          </div>
          <span className="ml-auto text-[#f8ffe8]/50 text-[12px]">
            {graduatedPaintDone ? graduatedItems.length : '…'}
          </span>
        </div>

        {!graduatedPaintDone || !gradSkelHoldDone ? (
          // SKELETON (compact rows)
          <ul className="divide-y divide-[#2a2d36]">
            {Array.from({ length: 3 }).map((_, i) => (
              <li
                key={i}
                className="py-2.5 flex items-center gap-3 animate-pulse"
              >
                {/* left: circle + two bars */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="h-6 w-6 rounded-full bg-gray-700" />
                  <div className="flex flex-col gap-1 w-[8rem]">
                    <div className="h-3 w-20 bg-gray-700 rounded" />
                    <div className="h-2 w-16 bg-gray-700 rounded" />
                  </div>
                </div>
                {/* green fees pill placeholder */}
                <div className="hidden md:block min-w-[96px] text-right">
                  <div className="inline-block h-4 w-20 bg-gray-700 rounded" />
                </div>
                {/* socials + star placeholders */}
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 bg-gray-700 rounded" />
                  <div className="h-4 w-4 bg-gray-700 rounded" />
                </div>
                <div className="ml-1 h-5 w-5 bg-gray-700 rounded" />
              </li>
            ))}
          </ul>
        ) : graduatedItems.length === 0 ? (
          <div className="text-[#f8ffe8]/60 text-[12px]">none yet</div>
        ) : (
          <>
            <ul className="divide-y divide-[#2a2d36]">
              {(graduatedByFees || graduatedItems)
                .slice(0, gradVisibleCount)
                .map(pool => (
                  <li
                    key={`${pool.contractAddress}:${pool.chainId}`}
                    className="py-1 mb-0 flex items-center gap-3 hover:bg-white/5 transition"
                  >
                    {/* left: logo + symbol + chain badge */}
                    <Link
                      to={buildBuyLink(
                        pool.chainId,
                        pool.contractAddress,
                        pool.versionType
                      )}
                      className="flex items-center gap-2 min-w-0 flex-1"
                    >
                      <TokenLogoImage
                        address={pool.contractAddress}
                        chainId={pool.chainId}
                        version={pool.versionType}
                        src={pool.logoUrl}
                        width={28}
                        height={28}
                        className="h-6 w-6 rounded-full flex-none"
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="font-semibold text-[#f500ff] text-[12px] truncate max-w-[10rem]">
                        {pool.tokenSymbol}
                      </span>
                      <img
                        src={pool.blockchainLogoUrl}
                        width={14}
                        height={14}
                        className="h-3.5 w-3.5 rounded-full opacity-80"
                        loading="lazy"
                        decoding="async"
                      />
                    </Link>

                    {/* fees (USD), green, k/M formatting, 1 decimal; fallback to dash if missing */}
                    <div className="text-[#7CFC98] tabular-nums text-right text-[13px] md:text-[16px] min-w-[72px] md:min-w-[96px] shrink-0">
                      {(() => {
                        const version = String(pool.versionType || '').toLowerCase()
                        if (version === 'v2') {
                          return (
                            <span
                              className="inline-flex items-center justify-end rounded-full bg-[#2a2d36] px-2 py-[2px] text-[10px] font-medium uppercase tracking-wide text-[#f8ffe8]/70"
                              title="V2 pool – fees not tracked"
                            >
                              V2 pool
                            </span>
                          )
                        }
                        const feeUsd =
                          pool.accumFeeUsd ??
                          pool.accumFeeUSD ??
                          pool.generatedFee ??
                          pool.feeUsd ??
                          pool.feesUsd ??
                          pool.feeUSD ??
                          0
                        const n = Number(feeUsd)
                        return n > 0 ? `$${formatMoney1(n)}` : '—'
                      })()}
                    </div>

                    {/* socials: Website, then X, then TG */}
                    <div className="flex items-center gap-2">
                      {pool.website ? (
                        <a
                          href={pool.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#f8ffe8]/70 hover:text-white text-[12px]"
                          title="Website"
                        >
                          🌐
                        </a>
                      ) : null}
                      {pool.twitter ? (
                        <a
                          href={pool.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#f8ffe8]/70 hover:text-white text-[12px]"
                          title="Twitter / X"
                        >
                          X
                        </a>
                      ) : null}
                      {pool.telegram ? (
                        <a
                          href={pool.telegram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#f8ffe8]/70 hover:text-white text-[12px]"
                          title="Telegram"
                        >
                          TG
                        </a>
                      ) : null}
                    </div>

                    {/* star toggle (consistent composite logic) */}
                    <button
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        toggleWatchlist(pool.contractAddress, pool.chainId)
                      }}
                      className="ml-1"
                      type="button"
                      aria-label="Toggle favorite"
                    >
                      {(() => {
                        const fav = isWatched(
                          watchlist,
                          pool.contractAddress,
                          pool.chainId
                        )
                        return (
                          <img
                            src={fav ? StarIcon : StarlineIcon}
                            width={16}
                            className="w-5 h-5 opacity-80 hover:opacity-100 transition-opacity"
                            alt={fav ? 'Unfavorite' : 'Favorite'}
                          />
                        )
                      })()}
                    </button>
                  </li>
                ))}
            </ul>

            {gradVisibleCount <
              (graduatedByFees && graduatedByFees.length
                ? graduatedByFees.length
                : graduatedItems.length) && (
              <div className="pt-2 text-right">
                <button
                  onClick={() =>
                    setGradVisibleCount(c =>
                      Math.min(
                        c + GRAD_PAGE_SIZE,
                        graduatedByFees && graduatedByFees.length
                          ? graduatedByFees.length
                          : graduatedItems.length
                      )
                    )
                  }
                  className="text-[12px] text-[#f8ffe8]/70 hover:text-white border border-[#2a2d36] rounded-lg px-3 py-1 bg-[#2a2d36]/40 hover:bg-[#2a2d36]"
                >
                  load more ↓
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  )
}
